import { UiElement } from "./UiElement.js";
import "./FeedbackWidget.css";

export { FeedbackWidget };

/**
 * Widget to send e-mails using a mailto-link
 * or a http post request
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.ui
 */
class FeedbackWidget extends UiElement {

    /**
     * options = {
     *   title: string,       // header of the widget
     *   description: string, // description text below title
     *   subject: string,     // subject of the e-mail
     *   target: string,      // mail address or path to php script
     *   post: boolean,       // if true, uses post request
     *   from: string,        // sender e-mail address
     *   captcha: string,     // path to php script for getting captcha URL
     * }
     * 
     * @param {string | HTMLElement} target 
     * @param {object} options 
     */
    constructor(target, options) {
        super(target)

        this.options_ = Object.assign({
            title: "",
            description: "",
            subject: "",
            target: "",

            position: "right",
            post: false,
            from: null,
            captcha: null
        }, options);

        this.initHTML_();
        this.close();
    }

    /**
     * initialize the DOM structure of the Element
     * 
     * @private
     */
    initHTML_() {
        this.setClass(["vef-widget-button", "feedback-widget", this.options_.position]);

        let html = `
            <div class="widget-button-icon far fa-comment-dots"></div>
            <div class="form">
                <span class="feedback-title">${this.options_.title || "FEEDBACK"}</span>
                <i class="fas fa-times"></i>
                <p>${this.options_.description || ""}</p>
                <textarea class="feedback-text" name="feedback" rows="4" cols="50"></textarea>
        `;

        if (this.options_.post) {
            html += `<p>Your E-Mail Address:<br/><input spellcheck="false" class="from" type="email"/></p>`;

            if (this.options_.captcha) {
                html += `<p>Captcha:<br/><img class="captcha-image"/><br/><input spellcheck="false" class="captcha" type="text"/></p>`;
            }
        }

        html += `<span class="response"></span><button class="btn-submit">Submit</button></div>`;

        this.setHtml(html);
        this.query(".widget-button-icon").addEventListener("click", () => this.open());
        this.query(".fa-times").addEventListener("click", () => this.close());
        this.query(".btn-submit").addEventListener("click", () => this.submit_());
    }

    /**
     * triggered by the submit button. open a mailto link
     * or create the post request for sending the message
     * 
     * @private
     */
    submit_() {
        const message = encodeURI(this.getElement().querySelector(".feedback-text").value.trim());
        const subject = encodeURI(`${this.options_.subject} <${window.location.href}>`.trim());

        if (this.options_.post) {
            const formData = new FormData();

            if (this.options_.captcha) formData.append('captcha', encodeURI(this.getElement().querySelector(".captcha").value.trim()));
            formData.append('from', encodeURI(this.getElement().querySelector(".from").value.trim()));

            formData.append('message', message);
            formData.append('subject', subject);

            let status;
            fetch(this.options_.target, { method: 'POST', body: formData })
                .then(response => {
                    status = response.status;
                    return response.text();
                })
                .then(text => {
                    const element = this.getElement().querySelector(".response");
                    element.innerText = text;
                    if (status == 200) {
                        element.classList.remove("fail");
                        this.close();
                    } else {
                        element.classList.add("fail");
                    }
                })
                .catch(e => { throw new Error(e) })
                .finally(() => {
                    if (this.options_.captcha) this.getCaptcha_();
                });
        } else {
            window.open(`mailto:${this.options_.target}?subject=${subject}>&body=${message}`);
            this.close();
        }
    }

    /**
     * Set the src for the captcha image using a php script
     * 
     * @private
     */
    getCaptcha_() {
        const element = this.getElement();
        const image = element.querySelector(".captcha-image");
        if (image) {
            element.querySelector(".captcha").value = "";
            fetch(this.options_.captcha)
                .then(response => response.text())
                .then(text => { image.src = text.trim() })
                .catch(e => { throw new Error(e) });
        }
    }

    /**
     * Open the widget
     */
    open() {
        if (this.options_.captcha) this.getCaptcha_();
        this.getElement().classList.add("maximized");
    }

    /**
     * Minimize the widget
     */
    close() {
        this.getElement().classList.remove("maximized");
    }

}